import React, { useEffect, useMemo } from 'react';
import type { RovoConfigProviderEntryProps } from '@jira/ai-rovo_actions-shared-types/src/RovoConfigProviderEntry.types.tsx';
import type { RovoConfiguration } from '@jira/ai-rovo_actions-shared-types/src/RovoConfiguration.types.tsx';
import type { RovoAction } from '@jira/ai-rovo_actions-shared-types/src/RovoAction.types.tsx';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import { defineMessage, type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import type { CapacityRovoConfigProviderEntrypointProps } from './CapacityRovoConfigProvider.entrypoint.types.tsx';
import { useIsProjectAdmin } from './useIsProjectAdmin.tsx';
import { useWfmSkillPrompt } from '@jira/servicedesk__workforce-optimization/src/useWfmSkillPrompt.tsx';

const listOverloadedAgentsPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'List agents who are overloaded',
	description:
		'WFM AI prompt for listing agents that are overloaded (over capacity) among the currently visible agents on the capacity page.',
	id: 'servicedesk-capacity.rovo.list-overloaded-agents',
});

const listIdleAgentsPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'List agents who are idle',
	description:
		'WFM AI prompt for listing agents that are idle (under capacity) among the currently visible agents on the capacity page.',
	id: 'servicedesk-capacity.rovo.list-idle-agents',
});

export const useCapacityRovoConfig = (): RovoConfiguration => {
	const { formatMessage } = useIntl();
	const { formatWfmSkillPrompt } = useWfmSkillPrompt();
	const isProjectAdmin = useIsProjectAdmin();

	return useMemo<RovoConfiguration>(
		() => ({
			getActions: async () => {
				if (!expVal('jsm-workforce-skill-experiment', 'isEnabled', false)) {
					return [];
				}

				const actions: RovoAction[] = [];

				if (isProjectAdmin) {
					actions.push({
						action: {
							id: 'wfm-list-overloaded-agents',
							title: formatMessage(listOverloadedAgentsPrompt),
							staticPrompt: formatWfmSkillPrompt(listOverloadedAgentsPrompt),
							icon: () => <AiAgentIcon label="" />,
							handleType: HandleActionType.Skill,
						},
					});

					actions.push({
						action: {
							id: 'wfm-list-idle-agents',
							title: formatMessage(listIdleAgentsPrompt),
							staticPrompt: formatWfmSkillPrompt(listIdleAgentsPrompt),
							icon: () => <AiAgentIcon label="" />,
							handleType: HandleActionType.Skill,
						},
					});
				}

				return Promise.all(
					actions.map(async ({ action, entryPointActions }) => {
						await entryPointActions?.load();
						return action;
					}),
				);
			},
			tags: expVal('jsm-workforce-skill-experiment', 'isEnabled', false)
				? { skillRegistryTags: ['manage-workforce'] }
				: { skillRegistryTags: [] },
		}),
		[formatMessage, formatWfmSkillPrompt, isProjectAdmin],
	);
};

export const CapacityRovoConfigProvider = ({
	setConfiguration,
}: CapacityRovoConfigProviderEntrypointProps): null => {
	const config = useCapacityRovoConfig();

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		setConfiguration(config);
	}, [config, setConfiguration]);

	return null;
};

export const CapacityRovoConfigProviderEntry = ({
	props: { setConfiguration },
}: RovoConfigProviderEntryProps): React.JSX.Element => (
	<CapacityRovoConfigProvider setConfiguration={setConfiguration} />
);

export default CapacityRovoConfigProviderEntry;
